import React, { useState, useEffect } from 'react';
import {Image, List, Popup} from 'antd-mobile';
import {ClockCircleOutlined, ExclamationCircleOutlined, PoweroffOutlined, SettingOutlined} from '@ant-design/icons';
import {useNavigate} from "react-router-dom";
import {getUserInfo, logout} from 'utils/auth';
import {t} from 'i18n';
import {RegisterAvatarIcon} from "../../../assets/icons";
import { getAppVersion } from 'utils/appRuntime';
import { getNavBarContainerStyle } from '../../../utils/deviceUtils';
import {formatLocalizedDate} from "../../../utils/dateFormatUtils";
import {PickupSettingsPath} from "../../../enums/PickUpNavigatePathEnum";


interface PickUpMenuDrawerProps {
    visible: boolean;
    onClose: () => void;
    failedCount: number;
}

const PickUpMenuDrawer: React.FC<PickUpMenuDrawerProps> = ({
                                                               visible,
                                                               onClose,
                                                               failedCount
                                                           }) => {
    const navigate = useNavigate();

    const [appVersion, setAppVersion] = useState<string>('V1.0');

    // 获取应用版本号
    useEffect(() => {
        getAppVersion().then(version => {
            setAppVersion(version);
        }).catch(error => {
            console.error('Failed to get app version:', error);
            setAppVersion('V1.0');
        });
    }, []);


    // 当前日期格式化
    const formattedDate = formatLocalizedDate(new Date(), {
        month: 'long',
        day: '2-digit',
        year: 'numeric'
    });

    const userInfo = getUserInfo();
    const userName = userInfo?.account.name;
    const userAvatar = userInfo?.account?.avatarUrl;

    return (
        <Popup
            position='left'
            visible={visible}
            onClose={onClose}
            closeOnMaskClick
        >
            <div
                {...getNavBarContainerStyle('p-4 h-full flex flex-col')}
            >
                {/* 用户信息区域 */}
                <div className="flex items-center mb-4">
                    <div
                        className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 text-xl overflow-hidden">
                        {userAvatar ? (
                            <Image
                                src={userAvatar}
                                width={64}
                                height={64}
                                fit='cover'
                                style={{borderRadius: 32}}
                            />
                        ) : (
                            <RegisterAvatarIcon className="w-12 h-12"/>
                        )}
                    </div>
                    <div className="ml-3">
                        <div className="font-medium text-base">{userName}</div>
                        <div className="text-gray-500 text-sm">{formattedDate}</div>
                    </div>
                </div>

                {/* 菜单项 */}
                <List
                    className="flex-grow"
                    style={{
                        '--border-bottom': 'none',
                        '--border-top': 'none',
                    }}>

                    <List.Item prefix={<ClockCircleOutlined/>} onClick={() => {
                        navigate("/pickup/finished")
                    }}>
                        {t('pages.pickUp.useMenu.isFinished')}
                    </List.Item>
                    <List.Item
                        prefix={<ExclamationCircleOutlined style={{color: 'red'}}/>}
                        extra={
                            failedCount > 0 ? (
                                <span
                                    className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">{failedCount > 99 ? '99+' : failedCount}</span>
                            ) : null}
                        onClick={() => {
                            navigate("/pickup/upload-failed")
                        }}
                    >
                        {t('pages.pickUp.useMenu.failedUploads')}
                    </List.Item>
                    <List.Item prefix={<SettingOutlined/>} onClick={() => {
                        navigate(PickupSettingsPath.PICKUP_SETTINGS)
                    }}>
                        {t('pages.pickUp.useMenu.settings')}
                    </List.Item>
                </List>

                {/* 底部区域 */}
                <div className="mt-auto w-full">
                    <List
                        style={{
                            '--border-top': 'none',
                        }}
                    >
                        <List.Item
                            prefix={<PoweroffOutlined/>}
                            onClick={async () => {
                                await logout('/login')
                            }}
                        >
                            {t('pages.pickUp.useMenu.signOut')}
                        </List.Item>
                    </List>
                    <div className="flex mt-2 p-2">
                        {t('pages.pickUp.useMenu.privacyPolicy')}
                    </div>
                    <div className="flex p-2 text-gray-500">
                        {`V${appVersion}`}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default PickUpMenuDrawer;